import { Button, Combobox, Option, Switch } from "@fluentui/react-components";
import {
  ArrowPathIcon,
  ArrowRightStartOnRectangleIcon,
  ArrowsRightLeftIcon,
  BanknotesIcon,
  BuildingOffice2Icon,
  ChartPieIcon,
  CpuChipIcon,
  HomeIcon,
  InformationCircleIcon,
  ShieldCheckIcon,
  TagIcon,
  TableCellsIcon,
  TrashIcon
} from "@heroicons/react/24/outline";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Brand } from "./Brand";
import { Footer } from "./Footer";
import { useAuth } from "../hooks/useAuth";
import { useCurrency } from "../context/CurrencyContext";
import { withForcedAzureRefresh } from "../lib/api";
import { signOutMicrosoft } from "../lib/microsoftAuth";
import { isAzureDataQueryKey } from "../lib/queryKeys";

interface AppShellProps {
  themeMode: "dark" | "light";
  onThemeChange: (value: "dark" | "light") => void;
}

const navSections = [
  {
    label: "Costs",
    items: [
      { to: "/dashboard", label: "Dashboard", icon: HomeIcon },
      { to: "/portfolio", label: "Portfolio", icon: BuildingOffice2Icon, microsoftOnly: true },
      { to: "/subscriptions", label: "Subscriptions", icon: TableCellsIcon }
    ]
  },
  {
    label: "Optimization",
    items: [
      { to: "/savings", label: "Savings", icon: BanknotesIcon },
      { to: "/performance", label: "Performance", icon: CpuChipIcon },
      { to: "/orphaned", label: "Orphaned Resources", icon: TrashIcon }
    ]
  },
  {
    label: "Governance",
    items: [
      { to: "/governance", label: "Governance Posture", icon: ShieldCheckIcon },
      { to: "/tag-allocation", label: "Tag Allocation", icon: TagIcon }
    ]
  },
  {
    label: "Product",
    items: [
      { to: "/about", label: "About", icon: InformationCircleIcon }
    ]
  }
];

export function AppShell({ themeMode, onThemeChange }: AppShellProps) {
  const { connectMicrosoftTenant, signOut, session, tenants } = useAuth();
  const { currency, toggleCurrency } = useCurrency();
  const queryClient = useQueryClient();
  const azureFetchCount = useIsFetching({
    predicate: (query) => isAzureDataQueryKey(query.queryKey)
  });
  const navigate = useNavigate();
  const [tenantSwitchError, setTenantSwitchError] = useState<string | null>(null);
  const [tenantSearch, setTenantSearch] = useState("");
  const [tenantSwitcherOpen, setTenantSwitcherOpen] = useState(false);
  const [tenantTransitionName, setTenantTransitionName] = useState<string | null>(null);
  const [isGlobalRefreshing, setIsGlobalRefreshing] = useState(false);
  const tenantList = tenants.data || [];
  const currentTenant = tenantList.find((tenant) => tenant.tenantId === session.data?.tenantId);
  const currentTenantLabel = currentTenant
    ? `${currentTenant.displayName}${
        currentTenant.defaultDomain && currentTenant.defaultDomain !== currentTenant.displayName
          ? ` · ${currentTenant.defaultDomain}`
          : ""
      }`
    : session.data?.tenantId || "Current tenant";
  const normalizedTenantSearch = tenantSearch.trim().toLowerCase();
  const filteredTenants = normalizedTenantSearch
    ? tenantList.filter((tenant) =>
        [tenant.displayName, tenant.defaultDomain, tenant.tenantId]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(normalizedTenantSearch))
      )
    : tenantList;

  useEffect(() => {
    if (!tenantTransitionName || connectMicrosoftTenant.isPending || azureFetchCount > 0) {
      return;
    }

    const timeoutId = window.setTimeout(() => setTenantTransitionName(null), 350);
    return () => window.clearTimeout(timeoutId);
  }, [azureFetchCount, connectMicrosoftTenant.isPending, tenantTransitionName]);

  async function handleSignOut() {
    const authType = session.data?.authType;
    await signOut.mutateAsync();

    if (authType === "microsoft") {
      await signOutMicrosoft();
      return;
    }

    navigate("/");
  }

  async function handleTenantChange(tenantId: string) {
    if (!tenantId || tenantId === session.data?.tenantId) {
      return;
    }

    try {
      const targetTenant = tenantList.find((tenant) => tenant.tenantId === tenantId);
      setTenantTransitionName(targetTenant?.displayName || tenantId);
      setTenantSwitchError(null);
      await connectMicrosoftTenant.mutateAsync(tenantId);
    } catch (error) {
      setTenantTransitionName(null);
      setTenantSwitchError(error instanceof Error ? error.message : "Unable to switch Azure tenant.");
    }
  }

  async function handleGlobalRefresh() {
    setIsGlobalRefreshing(true);

    try {
      await withForcedAzureRefresh(() =>
        queryClient.invalidateQueries({
          predicate: (query) => isAzureDataQueryKey(query.queryKey),
          refetchType: "active"
        })
      );
    } finally {
      setIsGlobalRefreshing(false);
    }
  }

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <Brand />
        <nav className="nav-list" aria-label="Main navigation">
          {navSections.map((section) => {
            const visibleItems = section.items.filter(
              (item) => !item.microsoftOnly || session.data?.authType === "microsoft"
            );

            return (
              <section className="nav-section" aria-label={section.label} key={section.label}>
                <span className="nav-section-label">{section.label}</span>
                <div className="nav-section-links">
                  {visibleItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <NavLink
                        className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                        to={item.to}
                        title={item.label}
                        key={item.to}
                      >
                        <Icon className="nav-icon" />
                        <span>{item.label}</span>
                      </NavLink>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </nav>
        <div className="sidebar-meter">
          <ChartPieIcon className="sidebar-meter-icon" />
          <span>Azure Native</span>
          <strong>{session.data?.authType === "microsoft" ? "Entra ID" : "Service Principal"}</strong>
        </div>
      </aside>
      <div className="app-main">
        <header className="topbar">
          <div>
            <span className="topbar-kicker">Azure FinOps Visibility</span>
            <strong>{session.data?.userName || "Connected Azure estate"}</strong>
          </div>
          <div className="topbar-actions">
            {session.data?.authType === "microsoft" ? (
              <div className="tenant-context">
                <BuildingOffice2Icon className="tenant-context-icon" aria-hidden="true" />
                <div className="tenant-context-field">
                  <label htmlFor="azure-tenant-switcher">Azure tenant</label>
                  {tenantList.length > 1 ? (
                    <Combobox
                      id="azure-tenant-switcher"
                      className="tenant-switcher-combobox"
                      aria-label="Search and switch Azure tenant"
                      appearance="underline"
                      inlinePopup
                      open={tenantSwitcherOpen}
                      value={tenantSwitcherOpen ? tenantSearch : currentTenantLabel}
                      selectedOptions={session.data.tenantId ? [session.data.tenantId] : []}
                      disabled={connectMicrosoftTenant.isPending}
                      placeholder="Search tenant by name, domain, or ID"
                      positioning={{ position: "below", align: "start", offset: 8 }}
                      listbox={{ className: "tenant-switcher-listbox" }}
                      onChange={(event) => setTenantSearch(event.target.value)}
                      onOpenChange={(_, optionData) => {
                        setTenantSwitcherOpen(optionData.open);
                        setTenantSearch("");
                      }}
                      onOptionSelect={(_, optionData) => {
                        if (optionData.optionValue) {
                          setTenantSwitcherOpen(false);
                          setTenantSearch("");
                          void handleTenantChange(optionData.optionValue);
                        }
                      }}
                    >
                      {filteredTenants.length ? (
                        filteredTenants.map((tenant) => {
                          const optionText = `${tenant.displayName}${
                            tenant.defaultDomain && tenant.defaultDomain !== tenant.displayName
                              ? ` · ${tenant.defaultDomain}`
                              : ""
                          }`;

                          return (
                            <Option
                              className="tenant-switcher-option"
                              value={tenant.tenantId}
                              text={optionText}
                              key={tenant.tenantId}
                            >
                              <span className="tenant-option-copy">
                                <strong>{tenant.displayName}</strong>
                                <span>{tenant.defaultDomain || tenant.tenantId}</span>
                              </span>
                            </Option>
                          );
                        })
                      ) : (
                        <Option className="tenant-switcher-empty" value="no-results" text="No tenants found" disabled>
                          No tenants match “{tenantSearch}”
                        </Option>
                      )}
                    </Combobox>
                  ) : (
                    <strong
                      className="tenant-current-name"
                      title={currentTenant?.tenantId || session.data.tenantId}
                    >
                      {tenants.isLoading
                        ? "Loading tenants..."
                        : currentTenant?.displayName || session.data.tenantId || "Current tenant"}
                    </strong>
                  )}
                </div>
                {connectMicrosoftTenant.isPending ? (
                  <span className="tenant-switcher-status">Switching tenant...</span>
                ) : null}
                {tenantSwitchError || tenants.error ? (
                  <span className="tenant-switcher-error" role="alert">
                    {tenantSwitchError || "Tenant list unavailable. Sign out and reconnect your Microsoft account."}
                  </span>
                ) : null}
              </div>
            ) : null}
            <Button
              appearance="subtle"
              disabled={isGlobalRefreshing || Boolean(tenantTransitionName)}
              icon={<ArrowPathIcon className={`button-icon ${isGlobalRefreshing ? "spinning" : ""}`} />}
              onClick={handleGlobalRefresh}
              title="Refresh Azure data shown on the current page"
            >
              {isGlobalRefreshing ? "Refreshing..." : "Refresh data"}
            </Button>
            <Button
              appearance="subtle"
              icon={<ArrowsRightLeftIcon className="button-icon" />}
              onClick={toggleCurrency}
              title={`Switch to ${currency === "USD" ? "EUR" : "USD"} using the latest ECB reference rate`}
            >
              {currency === "USD" ? "$ USD" : "€ EUR"}
            </Button>
            <Switch
              checked={themeMode === "light"}
              onChange={(_, data) => onThemeChange(data.checked ? "light" : "dark")}
              label={themeMode === "light" ? "Light" : "Dark"}
            />
            <Button appearance="subtle" icon={<ArrowRightStartOnRectangleIcon className="button-icon" />} onClick={handleSignOut}>
              Sign out
            </Button>
          </div>
        </header>
        {tenantTransitionName ? (
          <div className="tenant-loading-overlay" role="status" aria-live="polite" aria-busy="true">
            <div className="tenant-loading-card">
              <span className="tenant-loading-icon" aria-hidden="true">
                <ArrowPathIcon className="spinning" />
              </span>
              <div>
                <span className="eyebrow">
                  {connectMicrosoftTenant.isPending ? "Switching Azure tenant" : "Loading fresh Azure data"}
                </span>
                <strong>{tenantTransitionName}</strong>
                <p>
                  {connectMicrosoftTenant.isPending
                    ? "Connecting securely and clearing data from the previous tenant."
                    : "Waiting for costs, resources, and recommendations from the selected tenant."}
                </p>
              </div>
            </div>
          </div>
        ) : null}
        <main className="page-frame">
          <Outlet />
        </main>
        <Footer />
      </div>
    </div>
  );
}
